(() => {
  'use strict';

  const SELECTORS = {
    data: '[data-rb-products], [data-rb-data]',
    form: '[data-rb-form]',
    format: '[data-rb-format]',
    formula: '[data-rb-formula]',
    product: '[data-rb-product]',
    productSize: '[data-rb-product-size]',
    variant: '[data-rb-variant]',
    variantOption: '[data-rb-variant-option]',
    purchase: '[data-rb-purchase]',
    plan: '[data-rb-plan]',
    optionGroup: '[data-rb-options]',
    previewImage: '[data-rb-preview-image]',
    placeholder: '[data-rb-placeholder]',
    previewTitle: '[data-rb-preview-title]',
    previewSubtitle: '[data-rb-preview-subtitle]',
    previewPrice: '[data-rb-preview-price]',
    previewCompare: '[data-rb-preview-compare]',
    productLink: '[data-rb-product-link]',
    summary: '[data-rb-summary]',
    summaryImage: '[data-rb-summary-image]',
    summaryTitle: '[data-rb-summary-title]',
    summaryDetails: '[data-rb-summary-details]',
    summaryPrice: '[data-rb-summary-price]',
    summaryCompare: '[data-rb-summary-compare]',
    status: '[data-rb-status]',
    submit: '[data-rb-add]'
  };

  const asString = (value) => (value === null || value === undefined ? '' : String(value));

  const imageData = (image) => {
    if (!image) return null;
    if (typeof image === 'string') return { src: image, alt: '' };

    return {
      src: image.src || image.url || image.previewImage?.src || image.preview_image?.src || '',
      alt: image.alt || image.altText || ''
    };
  };

  const normalizeOption = (option, index, variant) => {
    if (typeof option === 'string') {
      return { name: `Option ${index + 1}`, value: option, position: index + 1 };
    }

    return {
      name: option?.name || `Option ${index + 1}`,
      value: option?.value || variant?.[`option${index + 1}`] || '',
      position: Number(option?.position || index + 1)
    };
  };

  const normalizePlan = (plan) => ({
    ...plan,
    id: asString(plan.id || plan.sellingPlanId || plan.selling_plan_id),
    name: plan.name || plan.title || plan.frequency || '',
    frequency: plan.frequency || plan.name || plan.title || '',
    price: Number(plan.price ?? plan.perDeliveryPrice ?? plan.per_delivery_price ?? 0),
    compareAtPrice: Number(plan.compareAtPrice ?? plan.compare_at_price ?? 0),
    available: plan.available !== false
  });

  const normalizeVariant = (variant) => {
    const rawOptions = Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3].filter(Boolean);

    return {
      ...variant,
      id: asString(variant.id),
      available: variant.available !== false,
      price: Number(variant.price || 0),
      compareAtPrice: Number(variant.compareAtPrice ?? variant.compare_at_price ?? 0),
      image: imageData(variant.featuredImage || variant.featured_image || variant.image),
      options: rawOptions.map((option, index) => normalizeOption(option, index, variant)),
      sellingPlans: (
        variant.sellingPlans ||
        variant.selling_plans ||
        variant.sellingPlanAllocations ||
        variant.selling_plan_allocations ||
        []
      ).map(normalizePlan)
    };
  };

  const normalizeProduct = (product) => ({
    ...product,
    id: asString(product.id),
    available: product.available !== false,
    format: product.format || product.productType || product.product_type || 'Products',
    formulaKey: asString(product.formulaKey || product.formula_key || product.groupKey || product.group_key || product.formula || product.id),
    formula: product.formula || product.title || '',
    sizeKey: product.sizeKey || product.size_key || product.slotKey || product.slot_key || '',
    sizeLabel: product.sizeLabel || product.size_label || product.slotLabel || product.slot_label || '',
    sizeHeading: product.sizeHeading || product.size_heading || '',
    variantHeading: product.variantHeading || product.variant_heading || '',
    defaultForFormula: product.defaultForFormula === true || product.isDefaultSize === true,
    defaultVariantId: asString(product.defaultVariantId || product.default_variant_id),
    subtitle: product.subtitle || product.description || '',
    image: imageData(product.featuredImage || product.featured_image || product.image),
    variants: (product.variants || []).map(normalizeVariant)
  });

  class RoutineBuilder extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;

      this._abortController = new AbortController();
      this._initialized = true;

      try {
        this.data = this.readData();
      } catch (error) {
        this.failInitialization(error);
        return;
      }

      this.products = (this.data.products || []).map(normalizeProduct);
      const formHook = this.querySelector(SELECTORS.form);
      this.nativeProductForm = formHook?.matches('product-form') ? formHook : formHook?.closest('product-form') || null;
      this.form = formHook?.matches('form') ? formHook : formHook?.querySelector('form') || this.querySelector('form[action*="/cart/add"]');
      this.summary = this.querySelector(SELECTORS.summary);
      this.status = this.querySelector(SELECTORS.status);
      this.submitButtons = [...this.querySelectorAll(SELECTORS.submit)];
      this.defaultSubmitText = this.data.labels?.addToCart || this.submitButtons[0]?.textContent.trim() || this.dataset.addLabel || 'Add to cart';
      this.state = this.getInitialState();
      this.isAdding = false;

      this.addEventListener('click', this.onClick.bind(this), { signal: this._abortController.signal });
      this.addEventListener('keydown', this.onKeydown.bind(this), { signal: this._abortController.signal });
      if (this.nativeProductForm) {
        this.form.addEventListener('submit', this.onNativeSubmit.bind(this), { signal: this._abortController.signal });
        this.form.addEventListener('variant:add', this.onNativeAdd.bind(this), { signal: this._abortController.signal });
        this.form.addEventListener('cart:error', this.onNativeError.bind(this), { signal: this._abortController.signal });
      }

      this.setupSummaryObserver();
      this.setupAnalyticsObserver();
      this.render({ announce: false });
      this.dataset.rbReady = 'true';
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._summaryObserver?.disconnect();
      this._viewObserver?.disconnect();
      this._summaryResizeObserver?.disconnect();
      this._initialized = false;
    }

    readData() {
      const script = this.querySelector(SELECTORS.data);
      if (!script) throw new Error('Routine Builder data is missing.');

      const parsed = JSON.parse(script.textContent || '{}');
      if (!Array.isArray(parsed.products) || parsed.products.length === 0) {
        throw new Error('Routine Builder has no configured products.');
      }

      return parsed;
    }

    failInitialization(error) {
      this.dataset.rbReady = 'error';
      const message = this.querySelector(SELECTORS.status);
      if (message) {
        message.hidden = false;
        message.textContent = this.dataset.setupError || 'This product builder is temporarily unavailable.';
      }

      if (window.Shopify?.designMode) console.error('[Routine Builder]', error);
    }

    getInitialState() {
      const pressedProduct = this.querySelector(`${SELECTORS.productSize}[aria-pressed="true"], ${SELECTORS.product}:not(${SELECTORS.formula})[aria-pressed="true"]`);
      const requestedProductId = pressedProduct?.dataset.productId || pressedProduct?.dataset.rbProduct || this.data.defaultProductId;
      const product = this.findProduct(requestedProductId) || this.firstPurchasableProduct() || this.products[0];
      const pressedVariant = this.querySelector(`${SELECTORS.variant}[aria-pressed="true"]`);
      const variant = this.findVariant(product, pressedVariant?.dataset.rbVariant || product.defaultVariantId) || this.firstPurchasableVariant(product) || product.variants[0];
      const requestedPurchase = this.querySelector(`${SELECTORS.purchase}[aria-pressed="true"]`)?.dataset.rbPurchase || this.dataset.defaultPurchase || this.data.defaultPurchase || 'one_time';
      const purchase = (product.requiresSellingPlan || requestedPurchase === 'subscription') && this.availablePlans(variant).length ? 'subscription' : 'one_time';
      const pressedPlan = this.querySelector(`${SELECTORS.plan}[aria-pressed="true"]`);
      const plan = this.findPlan(variant, pressedPlan?.dataset.planId || pressedPlan?.dataset.rbPlan) || this.availablePlans(variant)[0] || null;

      return {
        format: product.format,
        formulaKey: product.formulaKey || product.formula || product.id,
        product,
        variant,
        optionValues: this.optionValuesFromVariant(variant),
        purchase,
        plan,
        quantity: 1
      };
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;

      let step = '';
      let value = '';

      if (button.matches(SELECTORS.format)) {
        step = 'format';
        value = button.dataset.rbFormat || button.dataset.value || '';
        this.selectFormat(value);
      } else if (button.matches(SELECTORS.formula)) {
        step = 'formula';
        value = button.dataset.formulaKey || button.dataset.value || '';
        this.selectFormula(value);
      } else if (button.matches(SELECTORS.productSize)) {
        step = 'product_size';
        value = button.dataset.productId || button.dataset.value || '';
        this.selectProduct(value);
      } else if (button.matches(SELECTORS.product)) {
        step = 'product';
        value = button.dataset.productId || button.dataset.rbProduct || button.dataset.value || '';
        this.selectProduct(value);
      } else if (button.matches(SELECTORS.variant)) {
        step = 'variant';
        value = button.dataset.rbVariant || button.dataset.value || '';
        this.selectVariant(value);
      } else if (button.matches(SELECTORS.variantOption)) {
        step = button.dataset.optionName || `option_${button.dataset.optionPosition || '1'}`;
        value = button.dataset.value || button.dataset.rbVariantOption || '';
        this.selectVariantOption(Number(button.dataset.optionPosition || 1), value);
      } else if (button.matches(SELECTORS.purchase)) {
        step = 'purchase_type';
        value = button.dataset.rbPurchase || button.dataset.value || '';
        this.selectPurchase(value);
      } else if (button.matches(SELECTORS.plan)) {
        step = 'selling_plan';
        value = button.dataset.planId || button.dataset.rbPlan || button.dataset.value || '';
        this.selectPlan(value);
      } else {
        return;
      }

      event.preventDefault();
      this.render({ announce: true });
      this.track('routine_builder_select', { step, value });
      this.dispatchEvent(new CustomEvent('routine-builder:change', {
        bubbles: true,
        detail: { ...this.selectionDetail(), step, value }
      }));
    }

    onKeydown(event) {
      const button = event.target.closest(
        `${SELECTORS.format}, ${SELECTORS.product}, ${SELECTORS.variant}, ${SELECTORS.variantOption}, ${SELECTORS.purchase}, ${SELECTORS.plan}`
      );
      if (!button) return;

      const direction = ['ArrowRight', 'ArrowDown'].includes(event.key)
        ? 1
        : ['ArrowLeft', 'ArrowUp'].includes(event.key)
          ? -1
          : 0;
      const group = button.closest('[role="group"], fieldset, [data-rb-options]');
      if (!group || (!direction && !['Home', 'End'].includes(event.key))) return;

      const buttons = [...group.querySelectorAll('button')].filter((candidate) => !candidate.hidden && !candidate.disabled && candidate.offsetParent !== null);
      if (buttons.length < 2) return;

      event.preventDefault();
      let index = buttons.indexOf(button);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = buttons.length - 1;
      else index = (index + direction + buttons.length) % buttons.length;

      buttons[index].focus();
      buttons[index].click();
    }

    selectFormat(format) {
      if (!format) return;
      this.state.format = format;

      const currentMatches = this.state.product?.format === format && this.isProductPurchasable(this.state.product);
      if (!currentMatches) {
        const product = this.products.find((candidate) => candidate.format === format && candidate.defaultForFormula && this.isProductPurchasable(candidate)) ||
          this.products.find((candidate) => candidate.format === format && this.isProductPurchasable(candidate)) ||
          this.products.find((candidate) => candidate.format === format && candidate.defaultForFormula) ||
          this.products.find((candidate) => candidate.format === format);
        if (product) this.setProduct(product);
      }
    }

    selectFormula(formulaKey) {
      if (!formulaKey) return;
      const candidates = this.products.filter((product) => product.formulaKey === formulaKey);
      const product = candidates.find((candidate) => candidate.defaultForFormula && this.isProductPurchasable(candidate)) ||
        candidates.find((candidate) => this.isProductPurchasable(candidate)) ||
        candidates[0];
      if (product) this.setProduct(product);
    }

    selectProduct(productId) {
      const product = this.findProduct(productId);
      if (product) this.setProduct(product);
    }

    setProduct(product) {
      const variant = this.findVariant(product, product.defaultVariantId) || this.firstPurchasableVariant(product) || product.variants[0];
      this.state.product = product;
      this.state.format = product.format;
      this.state.formulaKey = product.formulaKey || product.formula || product.id;
      this.state.variant = variant;
      this.state.optionValues = this.optionValuesFromVariant(variant);
      this.ensureValidPurchaseAndPlan();
    }

    selectVariant(variantId) {
      const variant = this.findVariant(this.state.product, variantId);
      if (!variant) return;
      this.state.variant = variant;
      this.state.optionValues = this.optionValuesFromVariant(variant);
      this.ensureValidPurchaseAndPlan();
    }

    selectVariantOption(position, value) {
      if (!position || !value) return;

      const nextValues = { ...this.state.optionValues, [position]: value };
      const variants = this.state.product.variants;
      const exact = variants.find((variant) => this.variantMatchesOptions(variant, nextValues) && variant.available) ||
        variants.find((variant) => this.variantMatchesOptions(variant, nextValues));
      const fallback = variants.find((variant) => this.variantOptionValue(variant, position) === value && variant.available) ||
        variants.find((variant) => this.variantOptionValue(variant, position) === value);
      const variant = exact || fallback;

      if (!variant) return;
      this.state.variant = variant;
      this.state.optionValues = this.optionValuesFromVariant(variant);
      this.ensureValidPurchaseAndPlan();
    }

    selectPurchase(purchase) {
      if (purchase === 'subscription' && this.availablePlans(this.state.variant).length) {
        this.state.purchase = 'subscription';
        this.state.plan = this.findPlan(this.state.variant, this.state.plan?.id) || this.availablePlans(this.state.variant)[0];
      } else if (purchase === 'one_time' && !this.state.product.requiresSellingPlan) {
        this.state.purchase = 'one_time';
      }
    }

    selectPlan(planId) {
      const plan = this.findPlan(this.state.variant, planId);
      if (!plan) return;
      this.state.plan = plan;
      this.state.purchase = 'subscription';
    }

    ensureValidPurchaseAndPlan() {
      const plans = this.availablePlans(this.state.variant);
      if (!plans.length) {
        this.state.purchase = 'one_time';
        this.state.plan = null;
        return;
      }

      this.state.plan = this.findPlan(this.state.variant, this.state.plan?.id) || plans[0];
      if (this.state.product.requiresSellingPlan) this.state.purchase = 'subscription';
    }

    render({ announce = false } = {}) {
      if (!this.state.product || !this.state.variant) return;

      this.renderFormats();
      this.renderProducts();
      this.renderProductSizes();
      this.renderVariantGroups();
      this.renderVariantOptions();
      this.renderPurchaseOptions();
      this.renderPlanButtons();
      this.renderPlans();
      this.renderProductDetails();
      this.updateForm();
      this.updateSubmitButton();

      if (announce) this.announceSelection();
    }

    renderFormats() {
      this.querySelectorAll(SELECTORS.format).forEach((button) => {
        const value = button.dataset.rbFormat || button.dataset.value || '';
        const hasPurchasableProduct = this.products.some((product) => product.format === value && this.isProductPurchasable(product));
        button.setAttribute('aria-pressed', asString(value === this.state.format));
        button.disabled = !hasPurchasableProduct;
      });
    }

    renderProducts() {
      this.querySelectorAll(SELECTORS.formula).forEach((button) => {
        const formulaKey = asString(button.dataset.formulaKey || button.dataset.value);
        const products = this.products.filter((product) => product.formulaKey === formulaKey);
        const matchesFormat = products.some((product) => product.format === this.state.format);
        const available = products.some((product) => this.isProductPurchasable(product));
        button.hidden = !matchesFormat;
        button.setAttribute('aria-pressed', asString(formulaKey === this.state.formulaKey));
        button.disabled = !available;
      });
    }

    renderProductSizes() {
      const container = this.querySelector('[data-rb-product-size-options]');
      if (!container) return;

      const products = this.products.filter((product) => product.formulaKey === this.state.formulaKey);
      const fieldset = container.closest('[data-rb-step="product-size"], fieldset');
      if (fieldset) fieldset.hidden = products.length < 2;
      const title = fieldset?.querySelector('[data-rb-product-size-title]');
      if (title) title.textContent = this.state.product.sizeHeading || title.textContent;

      if (container.dataset.renderedFormula === this.state.formulaKey) {
        container.querySelectorAll(SELECTORS.productSize).forEach((button) => {
          const productId = asString(button.dataset.productId || button.dataset.value);
          const product = this.findProduct(productId);
          button.setAttribute('aria-pressed', asString(productId === this.state.product.id));
          button.disabled = !this.isProductPurchasable(product);
        });
        return;
      }

      const fragment = document.createDocumentFragment();
      products.forEach((product) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'routine-builder__option routine-builder__option--product-size';
        button.setAttribute('data-rb-product-size', '');
        button.setAttribute('data-rb-product', '');
        button.dataset.formulaKey = product.formulaKey;
        button.dataset.productId = product.id;
        button.dataset.value = product.id;
        button.setAttribute('aria-pressed', asString(product.id === this.state.product.id));
        button.disabled = !this.isProductPurchasable(product);

        const title = document.createElement('span');
        title.className = 'routine-builder__option-title';
        title.textContent = product.sizeLabel || product.title;
        button.append(title);

        const price = document.createElement('span');
        price.className = 'routine-builder__option-meta';
        const minimumPrice = Math.min(...product.variants.filter((variant) => variant.available).map((variant) => variant.price));
        price.textContent = `${this.data.labels?.from || 'From'} ${this.formatMoney(Number.isFinite(minimumPrice) ? minimumPrice : product.variants[0]?.price || 0)}`;
        button.append(price);
        fragment.append(button);
      });

      container.replaceChildren(fragment);
      container.dataset.renderedFormula = this.state.formulaKey;
    }

    renderVariantGroups() {
      const container = this.querySelector('[data-rb-variant-groups]');
      if (!container || container.dataset.renderedProduct === this.state.product.id) return;

      const options = this.meaningfulOptions(this.state.product);
      const hasSizeStep = this.products.filter((product) => product.formulaKey === this.state.formulaKey).length > 1;
      const startingStep = hasSizeStep ? 4 : 3;
      const fragment = document.createDocumentFragment();

      options.forEach((option, optionIndex) => {
        const position = Number(option.position || optionIndex + 1);
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'routine-builder__step';
        fieldset.dataset.rbStep = 'variant';
        fieldset.dataset.rbOptions = 'variant';
        fieldset.dataset.productId = this.state.product.id;
        fieldset.dataset.optionPosition = asString(position);

        const legend = document.createElement('legend');
        const stepLabel = document.createElement('span');
        stepLabel.className = 'routine-builder__step-label';
        stepLabel.textContent = `${this.data.labels?.step || 'Step'} ${startingStep + optionIndex}`;
        const title = document.createElement('span');
        title.className = 'routine-builder__step-title';
        title.textContent = this.state.product.variantHeading || `${this.data.labels?.choose || 'Choose'} ${asString(option.name || '').toLowerCase()}`;
        legend.append(stepLabel, title);

        const optionContainer = document.createElement('div');
        optionContainer.className = 'routine-builder__options routine-builder__options--variants';
        (option.values || []).forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'routine-builder__option routine-builder__option--variant';
          button.setAttribute('data-rb-variant-option', '');
          button.dataset.productId = this.state.product.id;
          button.dataset.optionPosition = asString(position);
          button.dataset.optionName = option.name || `Option ${position}`;
          button.dataset.value = value;
          button.setAttribute('aria-pressed', asString(this.state.optionValues[position] === value));
          const label = document.createElement('span');
          label.className = 'routine-builder__option-title';
          label.textContent = value;
          button.append(label);
          optionContainer.append(button);
        });

        fieldset.append(legend, optionContainer);
        fragment.append(fieldset);
      });

      container.replaceChildren(fragment);
      container.dataset.renderedProduct = this.state.product.id;

      const purchaseStep = this.querySelector('[data-rb-purchase-step-label]');
      if (purchaseStep) {
        purchaseStep.textContent = `${this.data.labels?.step || 'Step'} ${startingStep + options.length}`;
      }
    }

    renderVariantOptions() {
      this.querySelectorAll(`${SELECTORS.optionGroup}[data-rb-options="variant"]`).forEach((group) => {
        const productId = asString(group.dataset.productId);
        group.hidden = Boolean(productId && productId !== this.state.product.id);
      });

      this.querySelectorAll(SELECTORS.variant).forEach((button) => {
        const productId = asString(button.dataset.productId);
        const variant = this.findVariant(this.state.product, button.dataset.rbVariant || button.dataset.value);
        const visible = !productId || productId === this.state.product.id;
        button.hidden = !visible;
        button.disabled = !variant?.available;
        button.setAttribute('aria-pressed', asString(Boolean(variant && variant.id === this.state.variant.id)));
      });

      this.querySelectorAll(SELECTORS.variantOption).forEach((button) => {
        const productId = asString(button.dataset.productId);
        const position = Number(button.dataset.optionPosition || 1);
        const value = button.dataset.value || button.dataset.rbVariantOption || '';
        const visible = !productId || productId === this.state.product.id;
        const possible = this.hasAvailableVariantForOption(position, value);

        button.hidden = !visible;
        button.disabled = !possible;
        button.setAttribute('aria-pressed', asString(this.state.optionValues[position] === value));
      });
    }

    renderPurchaseOptions() {
      const hasSubscription = this.availablePlans(this.state.variant).length > 0;

      this.querySelectorAll(SELECTORS.purchase).forEach((button) => {
        const value = button.dataset.rbPurchase || button.dataset.value;
        button.disabled = (value === 'subscription' && !hasSubscription) || (value === 'one_time' && this.state.product.requiresSellingPlan);
        button.setAttribute('aria-pressed', asString(value === this.state.purchase));
      });
    }

    renderPlans() {
      const plans = this.availablePlans(this.state.variant);
      const planIds = new Set(plans.map((plan) => plan.id));

      this.querySelectorAll(SELECTORS.plan).forEach((button) => {
        const id = asString(button.dataset.planId || button.dataset.rbPlan || button.dataset.value);
        const productId = asString(button.dataset.productId);
        const variantId = asString(button.dataset.variantId);
        const belongsToSelection = (!productId || productId === this.state.product.id) && (!variantId || variantId === this.state.variant.id);
        const visible = belongsToSelection && planIds.has(id);

        button.hidden = !visible;
        button.disabled = !visible;
        button.setAttribute('aria-pressed', asString(Boolean(this.state.plan && id === this.state.plan.id)));
      });

      this.querySelectorAll(`${SELECTORS.optionGroup}[data-rb-options="plan"]`).forEach((group) => {
        const fieldset = group.closest('[data-rb-step="plan"], fieldset');
        if (fieldset) fieldset.hidden = this.state.purchase !== 'subscription' || plans.length < 2;
      });
    }

    renderPlanButtons() {
      const container = this.querySelector('[data-rb-plan-options], [data-rb-options="plan"]');
      if (!container) return;

      const plans = this.availablePlans(this.state.variant);
      const signature = `${this.state.variant.id}:${plans.map((plan) => plan.id).join(',')}`;
      if (container.dataset.renderedPlans === signature) return;

      const fragment = document.createDocumentFragment();
      plans.forEach((plan) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'routine-builder__option routine-builder__option--plan';
        button.setAttribute('data-rb-plan', '');
        button.dataset.planId = plan.id;
        button.dataset.value = plan.id;
        button.dataset.productId = this.state.product.id;
        button.dataset.variantId = this.state.variant.id;
        button.setAttribute('aria-pressed', asString(Boolean(this.state.plan && plan.id === this.state.plan.id)));

        const title = document.createElement('span');
        title.className = 'routine-builder__option-title';
        title.textContent = plan.name;
        button.append(title);

        if (plan.frequency && plan.frequency !== plan.name) {
          const meta = document.createElement('span');
          meta.className = 'routine-builder__option-meta';
          meta.textContent = plan.frequency;
          button.append(meta);
        }

        const price = document.createElement('span');
        price.className = 'routine-builder__option-price';
        price.textContent = this.formatMoney(plan.price);
        button.append(price);
        fragment.append(button);
      });

      container.replaceChildren(fragment);
      container.dataset.renderedPlans = signature;
    }

    renderProductDetails() {
      const product = this.state.product;
      const variant = this.state.variant;
      const image = variant.image?.src ? variant.image : product.image;
      const displayTitle = product.formula || product.title;
      const price = this.currentPrice();
      const compareAtPrice = this.currentCompareAtPrice();
      const details = this.summaryDetails();

      this.updateImage(this.querySelector(SELECTORS.previewImage), image, product.title);
      this.updateImage(this.querySelector(SELECTORS.summaryImage), image, product.title);
      this.setText(SELECTORS.previewTitle, displayTitle);
      this.setText(SELECTORS.previewSubtitle, product.subtitle || product.formula || '');
      this.renderPrice(SELECTORS.previewPrice, price, compareAtPrice);
      this.setText(SELECTORS.summaryTitle, displayTitle);
      this.setText(SELECTORS.summaryDetails, details);
      this.renderPrice(SELECTORS.summaryPrice, price, compareAtPrice);

      this.querySelectorAll(SELECTORS.productLink).forEach((link) => {
        if (product.url) link.href = product.url;
      });
    }

    updateForm() {
      if (!this.form) return;

      this.setFormValue('id', this.state.variant.id);
      this.setFormValue('quantity', asString(this.state.quantity || 1));
      this.setFormValue('selling_plan', this.state.purchase === 'subscription' ? this.state.plan?.id || '' : '');
    }

    setFormValue(name, value) {
      let input = this.form.elements.namedItem(name);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        this.form.append(input);
      }
      input.value = value;
      input.disabled = !value && name === 'selling_plan';
    }

    updateSubmitButton() {
      if (!this.submitButtons.length) return;

      const available = Boolean(this.state.variant?.available);
      this.submitButtons.forEach((button) => {
        button.disabled = !available || this.isAdding;
        button.setAttribute('aria-disabled', asString(!available || this.isAdding));
        button.setAttribute('aria-busy', asString(this.isAdding));
        const label = button.querySelector('[data-rb-add-label]') || button;

        if (this.isAdding) label.textContent = this.data.labels?.adding || this.dataset.addingLabel || 'Adding…';
        else if (!available) label.textContent = this.data.labels?.soldOut || this.dataset.soldOutLabel || 'Sold out';
        else label.textContent = this.defaultSubmitText;
      });
    }

    onNativeSubmit() {
      this.clearStatus();
      this.updateForm();

      // Let Prestige's <product-form> receive the original submitter before we
      // mark every associated CTA busy; its native handler owns the cart request.
      queueMicrotask(() => {
        this.isAdding = true;
        this.updateSubmitButton();
      });
    }

    onNativeAdd(event) {
      this.isAdding = false;
      this.updateSubmitButton();
      this.setStatus(this.data.labels?.added || this.dataset.addedLabel || 'Added to your cart.');
      this.dispatchEvent(new CustomEvent('routine-builder:add', {
        bubbles: true,
        detail: { ...this.selectionDetail(), items: event.detail?.items || [], cart: event.detail?.cart }
      }));
    }

    onNativeError(event) {
      this.isAdding = false;
      this.updateSubmitButton();
      const message = event.detail?.error || event.detail?.message || this.data.labels?.error || 'This item could not be added.';
      this.setStatus(message, true);
      this.track('routine_builder_error', { error_message: message });
    }

    setupSummaryObserver() {
      if (!this.summary) return;

      this.summary.hidden = false;
      this.summary.setAttribute('aria-hidden', 'true');
      this._summaryObserver = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        this.summary.classList.toggle('is-visible', visible);
        this.summary.setAttribute('aria-hidden', asString(!visible));
      }, { threshold: 0, rootMargin: '-8% 0px -8% 0px' });
      this._summaryObserver.observe(this);

      if ('ResizeObserver' in window) {
        this._summaryResizeObserver = new ResizeObserver(([entry]) => {
          this.style.setProperty('--rb-summary-height', `${Math.ceil(entry.contentRect.height)}px`);
        });
        this._summaryResizeObserver.observe(this.summary);
      }
    }

    setupAnalyticsObserver() {
      this._viewObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.track('routine_builder_view');
        this._viewObserver.disconnect();
      }, { threshold: 0.25 });
      this._viewObserver.observe(this);
    }

    track(eventName, extra = {}) {
      const detail = { event: eventName, ...this.selectionDetail(), ...extra };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(detail);
      this.dispatchEvent(new CustomEvent(`routine-builder:analytics:${eventName}`, { bubbles: true, detail }));
    }

    selectionDetail() {
      return {
        routine_builder_id: this.dataset.sectionId || this.data.sectionId || '',
        market: this.data.market || this.data.country || '',
        currency: this.data.currency || '',
        product_id: this.state.product?.id || '',
        product_handle: this.state.product?.handle || '',
        product_title: this.state.product?.title || '',
        variant_id: this.state.variant?.id || '',
        variant_sku: this.state.variant?.sku || '',
        purchase_type: this.state.purchase,
        selling_plan_id: this.state.purchase === 'subscription' ? this.state.plan?.id || '' : ''
      };
    }

    announceSelection() {
      const detail = `${this.state.product.title}, ${this.summaryDetails()}, ${this.formatMoney(this.currentPrice())}`;
      this.setStatus(detail);
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.hidden = false;
      this.status.textContent = message;
      this.status.dataset.error = asString(isError);
      this.status.setAttribute('role', isError ? 'alert' : 'status');
    }

    clearStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.dataset.error = 'false';
    }

    setupError() {
      this.setStatus(this.dataset.setupError || 'This product builder is temporarily unavailable.', true);
    }

    updateImage(element, image, fallbackAlt) {
      if (!element) return;
      const placeholder = element.closest('.routine-builder__media')?.querySelector(SELECTORS.placeholder);
      if (!image?.src) {
        element.hidden = true;
        if (placeholder) placeholder.hidden = false;
        return;
      }
      if (element.getAttribute('src') !== image.src) {
        element.removeAttribute('srcset');
        element.src = image.src;
      }
      element.alt = image.alt || fallbackAlt || '';
      element.hidden = false;
      if (placeholder) placeholder.hidden = true;
    }

    setText(selector, text, hidden = false) {
      this.querySelectorAll(selector).forEach((element) => {
        element.textContent = text || '';
        element.hidden = hidden || !text;
      });
    }

    renderPrice(selector, price, compareAtPrice) {
      this.querySelectorAll(selector).forEach((container) => {
        let current = container.querySelector('[data-rb-current-price]');
        let compare = container.querySelector('[data-rb-generated-compare]');
        if (!current) {
          container.replaceChildren();
          current = document.createElement('span');
          current.setAttribute('data-rb-current-price', '');
          compare = document.createElement('s');
          compare.setAttribute('data-rb-generated-compare', '');
          container.append(current, compare);
        }
        current.textContent = this.formatMoney(price);
        compare.textContent = compareAtPrice > price ? this.formatMoney(compareAtPrice) : '';
        compare.hidden = compareAtPrice <= price;
      });
    }

    currentPrice() {
      if (this.state.purchase === 'subscription' && this.state.plan) return this.state.plan.price;
      return this.state.variant?.price || 0;
    }

    currentCompareAtPrice() {
      if (this.state.purchase === 'subscription' && this.state.plan) {
        return this.state.plan.compareAtPrice || this.state.variant?.price || 0;
      }
      return this.state.variant?.compareAtPrice || 0;
    }

    meaningfulOptions(product) {
      if (!product || !Array.isArray(product.options)) return [];

      return product.options.filter((option) => {
        const values = [...new Set((option.values || []).map((value) => asString(value)).filter(Boolean))];
        return values.length > 1;
      });
    }

    summaryDetails() {
      const optionValues = this.state.variant.options.map((option) => option.value).filter((value) => value && value !== 'Default Title');
      const delivery = this.state.purchase === 'subscription'
        ? this.state.plan?.frequency || this.state.plan?.name || 'Subscription'
        : this.dataset.oneTimeLabel || 'One-time';
      return [this.state.product.sizeLabel, ...optionValues, delivery].filter(Boolean).join(' · ');
    }

    formatMoney(cents) {
      const value = Number(cents || 0);
      const format = this.data.moneyFormat || this.data.money_format;

      if (!format || !format.includes('{{')) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: this.data.currency || 'USD'
          }).format(value / 100);
        } catch (_error) {
          return `${(value / 100).toFixed(2)} ${this.data.currency || ''}`.trim();
        }
      }

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        const fixed = (number / 100).toFixed(precision);
        const [whole, fraction] = fixed.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return precision ? `${grouped}${decimal}${fraction}` : grouped;
      };

      const placeholder = format.match(/{{\s*(\w+)\s*}}/)?.[1] || 'amount';
      const replacements = {
        amount: formatWithDelimiters(value, 2),
        amount_no_decimals: formatWithDelimiters(value, 0),
        amount_with_comma_separator: formatWithDelimiters(value, 2, '.', ','),
        amount_no_decimals_with_comma_separator: formatWithDelimiters(value, 0, '.', ','),
        amount_with_apostrophe_separator: formatWithDelimiters(value, 2, "'", '.'),
        amount_no_decimals_with_space_separator: formatWithDelimiters(value, 0, ' ', '.'),
        amount_with_space_separator: formatWithDelimiters(value, 2, ' ', ',')
      };

      const formatted = format.replace(/{{\s*\w+\s*}}/, replacements[placeholder] || replacements.amount);
      if (!formatted.includes('<')) return formatted;
      const decoder = document.createElement('span');
      decoder.innerHTML = formatted;
      return decoder.textContent || formatted;
    }

    findProduct(id) {
      const target = asString(id);
      return target ? this.products.find((product) => product.id === target || product.handle === target) : null;
    }

    findVariant(product, id) {
      const target = asString(id);
      return product && target ? product.variants.find((variant) => variant.id === target) : null;
    }

    findPlan(variant, id) {
      const target = asString(id);
      return variant && target ? variant.sellingPlans.find((plan) => plan.id === target && plan.available) : null;
    }

    firstPurchasableProduct() {
      return this.products.find((product) => this.isProductPurchasable(product));
    }

    firstPurchasableVariant(product) {
      return product?.variants.find((variant) => variant.available) || null;
    }

    isProductPurchasable(product) {
      return Boolean(product?.available && this.firstPurchasableVariant(product));
    }

    availablePlans(variant) {
      return variant?.sellingPlans.filter((plan) => plan.available) || [];
    }

    optionValuesFromVariant(variant) {
      return (variant?.options || []).reduce((values, option, index) => {
        values[Number(option.position || index + 1)] = option.value;
        return values;
      }, {});
    }

    variantOptionValue(variant, position) {
      return variant?.options.find((option, index) => Number(option.position || index + 1) === Number(position))?.value || '';
    }

    variantMatchesOptions(variant, optionValues) {
      return Object.entries(optionValues).every(([position, value]) => this.variantOptionValue(variant, Number(position)) === value);
    }

    hasAvailableVariantForOption(position, value) {
      const otherValues = Object.fromEntries(
        Object.entries(this.state.optionValues).filter(([selectedPosition]) => Number(selectedPosition) !== Number(position))
      );

      return this.state.product.variants.some((variant) =>
        variant.available &&
        this.variantOptionValue(variant, position) === value &&
        this.variantMatchesOptions(variant, otherValues)
      ) || this.state.product.variants.some((variant) => variant.available && this.variantOptionValue(variant, position) === value);
    }
  }

  if (!customElements.get('routine-builder')) {
    customElements.define('routine-builder', RoutineBuilder);
  }

  window.PFMRoutineBuilder = RoutineBuilder;
})();
